// stores/ConflictStore.ts
import { makeAutoObservable, runInAction } from "mobx";
import localforage from "localforage";
import type RootStore from "./RootStore";
import {
  OfflineConfigRegistry,
  ConflictStrategy,
} from "./offline/OfflineConfigRegistry";

/**
 * Represents a single conflict between local and server data
 */
export interface Conflict<T = Record<string, unknown>> {
  id: string;
  storeName: string;
  entityId: string;
  local: T;
  server: T;
  timestamp: number;
  conflictingFields: string[];
  strategy: ConflictStrategy;
  resolved: boolean;
}

/**
 * Field-level resolution choice
 */
export type FieldResolution = "local" | "server";

/**
 * Resolution state for a conflict
 */
export interface ConflictResolutionState {
  conflictId: string;
  fieldChoices: Record<string, FieldResolution>;
  strategy: ConflictStrategy;
}

/**
 * MobX store for managing conflict resolution UI state.
 * Scans localforage for conflicts and provides methods for resolving them.
 */
class ConflictStore {
  // Core state
  conflicts: Conflict[] = [];
  isLoading = false;
  isResolving = false;
  error: string | null = null;

  // Selection state
  selectedConflictId: string | null = null;

  // Resolution state
  resolutionStates: Map<string, ConflictResolutionState> = new Map();

  // Dialog state
  isDialogOpen = false;

  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeAutoObservable(this, {
      rootStore: false,
    });

    // Load conflicts on initialization
    this.loadConflicts();
  }

  // ===========================================
  // COMPUTED PROPERTIES
  // ===========================================

  /**
   * Total number of unresolved conflicts
   */
  get conflictCount(): number {
    return this.conflicts.filter((c) => !c.resolved).length;
  }

  /**
   * Whether there are any unresolved conflicts
   */
  get hasConflicts(): boolean {
    return this.conflictCount > 0;
  }

  /**
   * Currently selected conflict
   */
  get selectedConflict(): Conflict | null {
    if (!this.selectedConflictId) return null;
    return this.conflicts.find((c) => c.id === this.selectedConflictId) || null;
  }

  /**
   * Resolution state for selected conflict
   */
  get selectedResolutionState(): ConflictResolutionState | null {
    if (!this.selectedConflictId) return null;
    return this.resolutionStates.get(this.selectedConflictId) || null;
  }

  /**
   * Conflicts grouped by store name
   */
  get conflictsByStore(): Record<string, Conflict[]> {
    const grouped: Record<string, Conflict[]> = {};
    for (const conflict of this.conflicts) {
      if (!conflict.resolved) {
        if (!grouped[conflict.storeName]) {
          grouped[conflict.storeName] = [];
        }
        grouped[conflict.storeName].push(conflict);
      }
    }
    return grouped;
  }

  /**
   * Get preview of merged data for selected conflict
   */
  get mergePreview(): Record<string, unknown> | null {
    const conflict = this.selectedConflict;
    const state = this.selectedResolutionState;

    if (!conflict || !state) return null;

    const merged: Record<string, unknown> = { ...conflict.server };

    for (const [field, choice] of Object.entries(state.fieldChoices)) {
      if (choice === "local") {
        merged[field] = (conflict.local as Record<string, unknown>)[field];
      }
    }

    return merged;
  }

  // ===========================================
  // ACTIONS
  // ===========================================

  /**
   * Load all conflicts from localforage
   */
  async loadConflicts(): Promise<void> {
    runInAction(() => {
      this.isLoading = true;
      this.error = null;
    });

    try {
      const conflicts: Conflict[] = [];

      // Scan localforage for conflict_ keys
      const keys = await localforage.keys();
      const conflictKeys = keys.filter((key) => key.startsWith("conflict_"));

      for (const key of conflictKeys) {
        try {
          const data = (await localforage.getItem(key)) as {
            local: Record<string, unknown>;
            server: Record<string, unknown>;
            timestamp: number;
          } | null;

          if (data) {
            // Parse store name and entity ID from key
            // Format: conflict_storeName_entityId
            const parts = key.replace("conflict_", "").split("_");
            const entityId = parts.pop() || "";
            const storeName = parts.join("_");

            // Calculate conflicting fields
            const conflictingFields = this.findConflictingFields(
              data.local,
              data.server
            );

            // Get strategy from registry
            const strategy =
              OfflineConfigRegistry.getConflictStrategy(storeName);

            conflicts.push({
              id: key,
              storeName,
              entityId,
              local: data.local,
              server: data.server,
              timestamp: data.timestamp,
              conflictingFields,
              strategy,
              resolved: false,
            });
          }
        } catch (parseError) {
          console.warn(`Failed to parse conflict ${key}:`, parseError);
        }
      }

      // Sort by timestamp (newest first)
      conflicts.sort((a, b) => b.timestamp - a.timestamp);

      runInAction(() => {
        this.conflicts = conflicts;
        this.isLoading = false;
      });
    } catch (error) {
      console.error("Failed to load conflicts:", error);
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to load conflicts";
        this.isLoading = false;
      });
    }
  }

  /**
   * Select a conflict for resolution
   */
  selectConflict(conflictId: string | null): void {
    runInAction(() => {
      this.selectedConflictId = conflictId;

      // Initialize resolution state if not exists
      if (conflictId && !this.resolutionStates.has(conflictId)) {
        const conflict = this.conflicts.find((c) => c.id === conflictId);
        if (conflict) {
          this.resolutionStates.set(conflictId, {
            conflictId,
            fieldChoices: {},
            strategy: conflict.strategy,
          });
        }
      }
    });
  }

  /**
   * Set field-level resolution choice
   */
  setFieldResolution(
    conflictId: string,
    field: string,
    choice: FieldResolution
  ): void {
    runInAction(() => {
      let state = this.resolutionStates.get(conflictId);

      if (!state) {
        const conflict = this.conflicts.find((c) => c.id === conflictId);
        state = {
          conflictId,
          fieldChoices: {},
          strategy: conflict?.strategy || "server",
        };
        this.resolutionStates.set(conflictId, state);
      }

      state.fieldChoices[field] = choice;
    });
  }

  /**
   * Resolve a conflict with the specified strategy
   */
  async resolveConflict(
    conflictId: string,
    strategy: "local" | "server" | "merge"
  ): Promise<void> {
    const conflict = this.conflicts.find((c) => c.id === conflictId);
    if (!conflict) {
      console.error("Conflict not found:", conflictId);
      return;
    }

    runInAction(() => {
      this.isResolving = true;
      this.error = null;
    });

    try {
      let resolvedData: Record<string, unknown>;

      switch (strategy) {
        case "local":
          resolvedData = {
            ...conflict.server,
            ...conflict.local,
            id: conflict.entityId,
            updatedAt: new Date().toISOString(),
          };
          break;

        case "server":
          resolvedData = conflict.server as Record<string, unknown>;
          break;

        case "merge":
          resolvedData = this.mergePreview || (conflict.server as Record<string, unknown>);
          break;

        default:
          resolvedData = conflict.server as Record<string, unknown>;
      }

      // Apply the resolution to the appropriate store
      await this.applyResolution(conflict.storeName, conflict.entityId, resolvedData);

      // Remove the conflict from localforage
      await localforage.removeItem(conflictId);

      // Update local state
      runInAction(() => {
        const index = this.conflicts.findIndex((c) => c.id === conflictId);
        if (index !== -1) {
          this.conflicts[index].resolved = true;
        }
        this.resolutionStates.delete(conflictId);

        // Clear selection if this was the selected conflict
        if (this.selectedConflictId === conflictId) {
          this.selectedConflictId = null;
        }

        this.isResolving = false;
      });

      // Notify user
      if (this.rootStore.notificationStore) {
        this.rootStore.notificationStore.addSuccessNotification(
          "Conflict resolved successfully"
        );
      }
    } catch (error) {
      console.error("Failed to resolve conflict:", error);
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to resolve conflict";
        this.isResolving = false;
      });

      if (this.rootStore.notificationStore) {
        this.rootStore.notificationStore.addErrorNotification(
          "Failed to resolve conflict"
        );
      }
    }
  }

  /**
   * Resolve all conflicts with server wins strategy
   */
  async resolveAllWithServer(): Promise<void> {
    const unresolvedConflicts = this.conflicts.filter((c) => !c.resolved);

    for (const conflict of unresolvedConflicts) {
      await this.resolveConflict(conflict.id, "server");
    }
  }

  /**
   * Resolve all conflicts with local wins strategy
   */
  async resolveAllWithLocal(): Promise<void> {
    const unresolvedConflicts = this.conflicts.filter((c) => !c.resolved);

    for (const conflict of unresolvedConflicts) {
      await this.resolveConflict(conflict.id, "local");
    }
  }

  /**
   * Open the conflict resolution dialog
   */
  openDialog(): void {
    runInAction(() => {
      this.isDialogOpen = true;
    });
  }

  /**
   * Close the conflict resolution dialog
   */
  closeDialog(): void {
    runInAction(() => {
      this.isDialogOpen = false;
      this.selectedConflictId = null;
    });
  }

  /**
   * Trigger a re-sync for a specific store
   */
  async triggerResync(storeName: string): Promise<void> {
    // Get the store from rootStore
    const store = this.rootStore[storeName];

    if (store && typeof store === "object" && "syncWithServer" in store) {
      try {
        await (store as { syncWithServer: () => Promise<void> }).syncWithServer();

        if (this.rootStore.notificationStore) {
          this.rootStore.notificationStore.addSuccessNotification(
            `Synced ${storeName} with server`
          );
        }
      } catch (error) {
        console.error(`Failed to sync ${storeName}:`, error);
        if (this.rootStore.notificationStore) {
          this.rootStore.notificationStore.addErrorNotification(
            `Failed to sync ${storeName}`
          );
        }
      }
    }
  }

  // ===========================================
  // PRIVATE METHODS
  // ===========================================

  /**
   * Find fields that differ between local and server versions
   */
  private findConflictingFields(
    local: Record<string, unknown>,
    server: Record<string, unknown>
  ): string[] {
    const conflicting: string[] = [];
    const allKeys = new Set([...Object.keys(local), ...Object.keys(server)]);

    for (const key of allKeys) {
      // Skip metadata fields
      if (key.startsWith("_") || key === "id" || key === "updatedAt" || key === "createdAt") {
        continue;
      }

      const localValue = local[key];
      const serverValue = server[key];

      if (!this.deepEqual(localValue, serverValue)) {
        conflicting.push(key);
      }
    }

    return conflicting;
  }

  /**
   * Deep equality check for values
   */
  private deepEqual(a: unknown, b: unknown): boolean {
    if (a === b) return true;
    if (a === null || b === null) return false;
    if (typeof a !== typeof b) return false;

    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      return a.every((val, i) => this.deepEqual(val, b[i]));
    }

    if (typeof a === "object" && typeof b === "object") {
      const aKeys = Object.keys(a as object);
      const bKeys = Object.keys(b as object);

      if (aKeys.length !== bKeys.length) return false;

      return aKeys.every((key) =>
        this.deepEqual(
          (a as Record<string, unknown>)[key],
          (b as Record<string, unknown>)[key]
        )
      );
    }

    return false;
  }

  /**
   * Apply resolved data to the appropriate store
   */
  private async applyResolution(
    storeName: string,
    entityId: string,
    resolvedData: Record<string, unknown>
  ): Promise<void> {
    // Get the store from rootStore
    const store = this.rootStore[storeName];

    if (store && typeof store === "object") {
      // Try to update the entity in the store
      if ("addOrUpdateEvent" in store) {
        (store as { addOrUpdateEvent: (data: Record<string, unknown>) => void }).addOrUpdateEvent(
          resolvedData
        );
      }

      // If it's an OfflineServiceStore, update via the patch method
      if ("patch" in store && typeof (store as Record<string, unknown>).patch === "function") {
        try {
          await (store as { patch: (id: string, data: Record<string, unknown>) => Promise<unknown> }).patch(
            entityId,
            resolvedData
          );
        } catch (error) {
          // If patch fails (e.g., network issue), the local update will still apply
          console.warn("Could not sync resolution to server:", error);
        }
      }
    }
  }

  /**
   * Clean up resolved conflicts from state
   */
  cleanup(): void {
    runInAction(() => {
      this.conflicts = this.conflicts.filter((c) => !c.resolved);
    });
  }
}

export default ConflictStore;
